import { CSSProperties } from "react";
import { CSSPixelValue } from "../../types/CSSPixelValue";
import { coerceCssPixelValue } from "../../utils/coerceCssPixelValue";

// Shorthand props 타입
interface ShorthandProps {
  // Margin shorthands
  m?: CSSPixelValue; // margin
  mt?: CSSPixelValue; // marginTop
  mb?: CSSPixelValue; // marginBottom
  ml?: CSSPixelValue; // marginLeft
  mr?: CSSPixelValue; // marginRight
  mx?: CSSPixelValue; // marginLeft + marginRight
  my?: CSSPixelValue; // marginTop + marginBottom

  // Padding shorthands
  p?: CSSPixelValue; // padding
  pt?: CSSPixelValue; // paddingTop
  pb?: CSSPixelValue; // paddingBottom
  pl?: CSSPixelValue; // paddingLeft
  pr?: CSSPixelValue; // paddingRight
  px?: CSSPixelValue; // paddingLeft + paddingRight
  py?: CSSPixelValue; // paddingTop + paddingBottom

  // Size shorthands
  w?: CSSPixelValue; // width
  h?: CSSPixelValue; // height
  minW?: CSSPixelValue;
  minH?: CSSPixelValue;
  maxW?: CSSPixelValue;
  maxH?: CSSPixelValue;

  // Background shorthand
  bg?: string; // background
  bgColor?: string; // backgroundColor
}

// CSS Properties를 CSSPixelValue로 변환 가능하게
interface CSSPixelProps {
  width?: CSSPixelValue;
  height?: CSSPixelValue;
  minWidth?: CSSPixelValue;
  minHeight?: CSSPixelValue;
  maxWidth?: CSSPixelValue;
  maxHeight?: CSSPixelValue;
  margin?: CSSPixelValue;
  marginTop?: CSSPixelValue;
  marginBottom?: CSSPixelValue;
  marginLeft?: CSSPixelValue;
  marginRight?: CSSPixelValue;
  padding?: CSSPixelValue;
  paddingTop?: CSSPixelValue;
  paddingBottom?: CSSPixelValue;
  paddingLeft?: CSSPixelValue;
  paddingRight?: CSSPixelValue;
  top?: CSSPixelValue;
  bottom?: CSSPixelValue;
  left?: CSSPixelValue;
  right?: CSSPixelValue;
  borderRadius?: CSSPixelValue;
}

// 나머지 모든 CSS 속성
type OtherCSSProps = Omit<
  CSSProperties,
  keyof CSSPixelProps | keyof ShorthandProps | "direction" // direction은 BaseStack prop으로 사용
>;

interface BaseStackProps extends ShorthandProps, CSSPixelProps, OtherCSSProps {
  /** Stack 방향: vertical (기본) 또는 horizontal */
  direction?: "vertical" | "horizontal";
  /** 자식 요소 간 간격 (CSSPixelValue). 기본값 0 */
  spacing?: CSSPixelValue;
  /** 렌더링할 HTML 요소 */
  as?: keyof JSX.IntrinsicElements;
  /** children */
  children?: React.ReactNode;
  /** style override */
  style?: CSSProperties;
  /** className */
  className?: string;
}

type Props<Element extends keyof JSX.IntrinsicElements = "div"> =
  BaseStackProps &
    Omit<JSX.IntrinsicElements[Element], keyof BaseStackProps | "as">;

/**
 * BaseStack - GOI 프로젝트용 Stack 컴포넌트
 *
 * Chakra UI의 자동 margin 문제를 해결한 순수 flexbox 기반 Stack:
 * - 기본 spacing: 0 (명시적 spacing 관리)
 * - direction prop으로 vertical/horizontal 선택
 * - CSS gap 속성 사용 (Chakra UI의 margin 로직 완전 우회)
 * - Pure JSX 요소 사용 (Chakra UI 의존성 없음)
 * - 모든 CSS 속성 지원 (Chakra Box 수준)
 * - Shorthand props 지원 (mt, px, bg 등)
 *
 * @example
 * // 수직 스택 (기본) - Spacing 컴포넌트 사용
 * <BaseStack>
 *   <Component1 />
 *   <Spacing gap={16} />
 *   <Component2 />
 * </BaseStack>
 *
 * @example
 * // 수평 스택 - spacing prop 사용
 * <BaseStack direction="horizontal" spacing={8}>
 *   <Chip1 />
 *   <Chip2 />
 * </BaseStack>
 *
 * @example
 * // Shorthand props 사용
 * <BaseStack mt={16} px={20} bg={colors.white}>
 *   <Component />
 * </BaseStack>
 */
function BaseStack<Element extends keyof JSX.IntrinsicElements = "div">(
  props: Props<Element>
) {
  const {
    // BaseStack specific
    direction = "vertical",
    spacing = 0,
    as: component = "div",

    // Shorthand props
    m,
    mt,
    mb,
    ml,
    mr,
    mx,
    my,
    p,
    pt,
    pb,
    pl,
    pr,
    px,
    py,
    w,
    h,
    minW,
    minH,
    maxW,
    maxH,
    bg,
    bgColor,

    // CSS Pixel props
    width,
    height,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    top,
    bottom,
    left,
    right,
    borderRadius,

    // Other props
    children,
    style,
    className,
    ...rest
  } = props;

  const Component = component as any;

  // Shorthand → Full prop 변환
  const resolvedMargin = coerceCssPixelValue(m ?? margin);
  const resolvedMarginTop = coerceCssPixelValue(mt ?? my ?? marginTop);
  const resolvedMarginBottom = coerceCssPixelValue(mb ?? my ?? marginBottom);
  const resolvedMarginLeft = coerceCssPixelValue(ml ?? mx ?? marginLeft);
  const resolvedMarginRight = coerceCssPixelValue(mr ?? mx ?? marginRight);

  const resolvedPadding = coerceCssPixelValue(p ?? padding);
  const resolvedPaddingTop = coerceCssPixelValue(pt ?? py ?? paddingTop);
  const resolvedPaddingBottom = coerceCssPixelValue(pb ?? py ?? paddingBottom);
  const resolvedPaddingLeft = coerceCssPixelValue(pl ?? px ?? paddingLeft);
  const resolvedPaddingRight = coerceCssPixelValue(pr ?? px ?? paddingRight);

  const resolvedWidth = coerceCssPixelValue(w ?? width);
  const resolvedHeight = coerceCssPixelValue(h ?? height);
  const resolvedMinWidth = coerceCssPixelValue(minW ?? minWidth);
  const resolvedMinHeight = coerceCssPixelValue(minH ?? minHeight);
  const resolvedMaxWidth = coerceCssPixelValue(maxW ?? maxWidth);
  const resolvedMaxHeight = coerceCssPixelValue(maxH ?? maxHeight);

  const resolvedBackground = bg ?? rest.background;
  const resolvedBackgroundColor = bgColor ?? rest.backgroundColor;

  // spacing → gap 변환
  const gapValue = coerceCssPixelValue(spacing);

  // 모든 CSS props를 style에 병합
  const computedStyle: CSSProperties = {
    display: "flex",
    flexDirection: direction === "horizontal" ? "row" : "column",
    gap: gapValue,

    // Resolved props
    margin: resolvedMargin,
    marginTop: resolvedMarginTop,
    marginBottom: resolvedMarginBottom,
    marginLeft: resolvedMarginLeft,
    marginRight: resolvedMarginRight,
    padding: resolvedPadding,
    paddingTop: resolvedPaddingTop,
    paddingBottom: resolvedPaddingBottom,
    paddingLeft: resolvedPaddingLeft,
    paddingRight: resolvedPaddingRight,
    width: resolvedWidth,
    height: resolvedHeight,
    minWidth: resolvedMinWidth,
    minHeight: resolvedMinHeight,
    maxWidth: resolvedMaxWidth,
    maxHeight: resolvedMaxHeight,
    top: coerceCssPixelValue(top),
    bottom: coerceCssPixelValue(bottom),
    left: coerceCssPixelValue(left),
    right: coerceCssPixelValue(right),
    borderRadius: coerceCssPixelValue(borderRadius),
    background: resolvedBackground,
    backgroundColor: resolvedBackgroundColor,

    // 나머지 모든 CSS props (rest에서 자동으로)
    ...rest,

    // style prop으로 override 가능
    ...style,
  };

  // undefined 값을 제거하여 SSR/CSR 일관성 보장
  const cleanStyle = Object.fromEntries(
    Object.entries(computedStyle).filter(([_, value]) => value !== undefined)
  ) as CSSProperties;

  return (
    <Component style={cleanStyle} className={className} {...rest}>
      {children}
    </Component>
  );
}

export { BaseStack };
export type { BaseStackProps, Props };
